/**
 * Utility functions -- a mixed bag of stuff.
 */
import { Trans } from "react-i18next";
import { t } from "i18next";
import * as React from "react";
import { Stack, Text } from "@chakra-ui/react";
import moment from "moment";
import GLOBAL from "../context/global";
import { urlEncode } from "api/api-utils";

/**
 * Check if a value is defined and not null.
 * @param {*} value
 * @returns {boolean} true for truthy and also false, "", 0, false for null, undefined
 */
export function is(value) {
  return value !== null && value !== undefined;
}

/**
 * De-duplicate an array of objects by a given key, keeping the first occurrence.
 *
 * If you just need to dedupe a list, use [...new Set(mylist)]
 * @param {?Object[]} array
 * @param {string|function} key
 * @returns {?Object[]}
 */
export function dedupe(array, key) {
  if (!array) return array;
  const valueForKeyFn = typeof key === "function" ? key : (item) => item[key];
  return array.filter((item, index, self) => index === self.findIndex((t) => valueForKeyFn(t) === valueForKeyFn(item)));
}

export function deepCopy(obj) {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Use this in preference to direct string manipulation because it handles encoding.
 *
 * Modify the current URL with the given params. Return the new URL.
 * This just computes the new URL, the actual page is not changed.
 * @param {object} params Unescaped key-value pairs
 * @param {string?} url If not provided, the current URL is used.
 */
export function modifiedUrl(params, url) {
  if (!url) url = window.location.href;
  // handle relative urls
  const DUMMY_COM = "http://dummy.com";
  if (!url.includes("://")) url = DUMMY_COM + url;
  const newUrl = new URL(url);
  Object.entries(params).forEach(([key, value]) => {
    if (value || value === 0 || value === false) {
      newUrl.searchParams.set(key, value);
    } else {
      newUrl.searchParams.delete(key);
    }
  });
  let newString = newUrl.toString();
  if (newString.startsWith(DUMMY_COM)) {
    newString = newString.substring(DUMMY_COM.length);
  }
  return newString;
}

/**
 * Convert an absolute URL _for this web-app_ to a relative URL.
 * Does nothing if the URL is not absolute or not for this web-app.
 * @param {string} href The URL to convert.
 * @returns {string} The relative URL.
 */
export function relativeUrl(href) {
  if (href.startsWith(window.location.origin)) {
    href = href.substring(window.location.origin.length);
  }
  return href;
}

/**
 * Get the URL params as an object.
 * @see useSearchParams from react-router-dom
 * @param {string?} url If not provided, the current URL is used.
 * @returns {object} {string: string}
 */
export function getUrlParams(url) {
  const u = new URL(url || window.location.href);
  return Object.fromEntries(u.searchParams.entries());
}
export function setUrlParams(url, params) {
  const u = new URL(url || window.location.href);
  Object.entries(params).forEach(([key, value]) => {
    u.searchParams.set(key, value);
  });
  return u.toString();
}
window.setUrlParams = setUrlParams; // debug

export function getObjValueFromPath(path, obj, separator = ".") {
  const properties = Array.isArray(path) ? path : path.split(separator);
  return properties.reduce((prev, curr) => prev && prev[curr], obj);
}

export function generateArrayOfYears() {
  const max = new Date().getFullYear() + 3;
  const min = 2021;
  const years = [];

  for (let i = max; i >= min; i--) {
    years.push(i);
  }
  return years;
}

function showToast(toast, message, status, id = "toast") {
  if (!toast || toast.isActive(id)) {
    return;
  }
  toast({
    title: message,
    status: status,
    duration: 5000,
    isClosable: true,
    id: id,
  });
}

export function showSuccessToast(toast, message, id = "success-toast") {
  showToast(toast, message, "success", id);
}
export function showInfoToast(toast, message, id = "info-toast") {
  showToast(toast, message, "info", id);
}

/**
 * Show an error toast.
 * @param {*} toast
 * @param {string|Error|object} message
 * @param {string?} id
 * @returns
 */
// TODO we have lots of repeated code that sets onError for API requests (onError repeated more-or-less-the-same 188 times in this app!)
// which leads to inconsistency, more bugs, and higher maintenance costs.
// onError calling this function should be standardised -- probably in the axios wrapper.
export function showErrorToast(toast, message, id = "error-toast") {
  console.log("showErrorToast", message);
  // HACK handle axios wrapped errors, Error objects, and plain strings
  let msg = message.response?.data?.parameters?.message || message.message || message;
  showToast(toast, msg, "error", id);
}

export function showWarnToast(toast, message, id = "warn-toast") {
  showToast(toast, message, "warning", id);
}

export function isScopeThreeVisible(questionnaireData) {
  return (
    questionnaireData.configurations.purchasedGoodsAndServicesShouldBeFilled ||
    questionnaireData.configurations.capitalGoodsShouldBeFilled ||
    questionnaireData.configurations.fuelAndEnergyRelatedActivitiesShouldBeFilled ||
    questionnaireData.configurations.upstreamTransportationAndDistributionShouldBeFilled ||
    questionnaireData.configurations.wasteGeneratedInOperationsShouldBeFilled ||
    questionnaireData.configurations.businessTravelShouldBeFilled ||
    questionnaireData.configurations.employeeCommutingShouldBeFilled ||
    questionnaireData.configurations.upstreamLeasedAssetsShouldBeFilled ||
    questionnaireData.configurations.downstreamTransportationAndDistributionShouldBeFilled ||
    questionnaireData.configurations.processingOfSoldProductsShouldBeFilled ||
    questionnaireData.configurations.useOfSoldProductsShouldBeFilled ||
    questionnaireData.configurations.treatmentOfSoldProductsShouldBeFilled ||
    questionnaireData.configurations.downstreamLeasedAssetsShouldBeFilled ||
    questionnaireData.configurations.franchisesShouldBeFilled ||
    questionnaireData.configurations.investmentsShouldBeFilled
  );
}

/**
 * @deprecated
 * @returns {
 *  financialYear: string for the previous year eg "2024"
 *  quarter: "ALL",
 * }
 */
export function calculateDefaultValues() {
  const currentDate = new Date();
  return {
    financialYear: (currentDate.getFullYear() - 1).toString(),
    quarter: "ALL",
  };
}

export function prepareCategoryDefaultValues(items, numericAttributes) {
  return items.map((item) => {
    let updatedAttributes = {};
    numericAttributes.forEach(
      (numAttr) => (updatedAttributes[numAttr] = item[numAttr] != null ? item[numAttr].toString() : "")
    );
    return {
      ...item,
      ...updatedAttributes,
    };
  });
}

export function translateSubElementString(translationKey) {
  return <Trans i18nKey={translationKey} components={{ sub: <sub /> }} />;
}

export const getMonthsShortNames = (translationMethod) => {
  return [
    translationMethod("monthsShortNames.January"),
    translationMethod("monthsShortNames.February"),
    translationMethod("monthsShortNames.March"),
    translationMethod("monthsShortNames.April"),
    translationMethod("monthsShortNames.May"),
    translationMethod("monthsShortNames.June"),
    translationMethod("monthsShortNames.July"),
    translationMethod("monthsShortNames.August"),
    translationMethod("monthsShortNames.September"),
    translationMethod("monthsShortNames.October"),
    translationMethod("monthsShortNames.November"),
    translationMethod("monthsShortNames.December"),
  ];
};

export const getWeekDaysShortNames = (translationMethod) => {
  return [
    translationMethod("weekDaysShortNames.Sunday"),
    translationMethod("weekDaysShortNames.Monday"),
    translationMethod("weekDaysShortNames.Tuesday"),
    translationMethod("weekDaysShortNames.Wednesday"),
    translationMethod("weekDaysShortNames.Thursday"),
    translationMethod("weekDaysShortNames.Friday"),
    translationMethod("weekDaysShortNames.Saturday"),
  ];
};

export const dimensionsKeys = ["dimensionOne", "dimensionTwo", "dimensionThree", "dimensionFour", "dimensionFive"];

export const numberFormatter = (num, digits) => {
  const lookup = [
    { value: 1, symbol: "" },
    { value: 1e3, symbol: "k" },
    { value: 1e6, symbol: "M" },
    { value: 1e9, symbol: "B" },
    { value: 1e12, symbol: "T" },
    { value: 1e15, symbol: "P" },
    { value: 1e18, symbol: "E" },
  ];
  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
  const item = lookup
    .slice()
    .reverse()
    .find((item) => {
      return Math.abs(num) >= item.value;
    });
  return item ? `${(num / item.value).toFixed(digits).replace(rx, "$1")}${item.symbol}` : "0";
};

export const QUESTIONNAIRE_STATUSES = Object.freeze({
  FINALIZED: "FINALIZED",
  NEW: "NEW",
  IN_PROGRESS: "IN_PROGRESS",
});

export const nonNumericSymbols = ["e", "E"];
export const nonIntegerSymbols = ["e", "E", ",", ".", "-", "+"];

export const greenhouseGases = {
  CarbonDioxide: {
    label: translateSubElementString("greenhouseGasses.carbonDioxide.formula"),
    title: t("greenhouseGasses.carbonDioxide.title"),
  },
  Methane: {
    label: translateSubElementString("greenhouseGasses.methane.formula"),
    title: t("greenhouseGasses.methane.title"),
  },
  NitrousOxide: {
    label: translateSubElementString("greenhouseGasses.nitrousOxide.formula"),
    title: t("greenhouseGasses.nitrousOxide.title"),
  },
  Hydrofluorocarbons: {
    label: translateSubElementString("greenhouseGasses.hydrofluorocarbons.formula"),
    title: t("greenhouseGasses.hydrofluorocarbons.title"),
  },
  Perfluorocarbons: {
    label: translateSubElementString("greenhouseGasses.perfluorocarbons.formula"),
    title: t("greenhouseGasses.perfluorocarbons.title"),
  },
  SulphurHexafluoride: {
    label: translateSubElementString("greenhouseGasses.sulphurHexafluoride.formula"),
    title: t("greenhouseGasses.sulphurHexafluoride.title"),
  },
  CarbonDioxideEquivalent: {
    label: translateSubElementString("greenhouseGasses.carbonDioxideEquivalent.formula"),
    title: t("greenhouseGasses.carbonDioxideEquivalent.title"),
  },
  NitrogenTrifluoride: {
    label: translateSubElementString("greenhouseGasses.nitrogenTrifluoride.formula"),
    title: t("greenhouseGasses.nitrogenTrifluoride.title"),
  },
};

export const CURRENCY_SYMBOLS = Object.freeze({
  GBP: "£",
  USD: "$",
  EUR: "€",
});

export const USER_ROLES = Object.freeze({
  ORG_ADMIN: "ORG_ADMIN",
  ORG_VIEWER: "ORG_VIEWER",
  ORG_MEMBER: "ORG_MEMBER",
  CSRD_MEMBER: "CSRD_MEMBER",
  SUPPLIER_ADMIN: "SUPPLIER_ADMIN",

  SUPPLIER: "SUPPLIER",
  SUPPLIER_VIEWER: "SUPPLIER_VIEWER",
});

export const USER_TYPES = Object.freeze({
  ORG_MEMBER: "ORG_MEMBER",
  SUPPLIER: "SUPPLIER",
});

export const USER_STATUSES = Object.freeze({
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
  DEACTIVATED: "DEACTIVATED",
});

export const SUPPLIER_STATUSES = Object.freeze({
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
  DEACTIVATED: "DEACTIVATED",
});

export const VALIDATION_PATTERNS = Object.freeze({
  EMAIL_ADDRESS: {
    value: /^[\w-.+]+@([\w-]+\.)+[\w-]{2,4}$/,
    message: t("validationErrors.emailAddress"),
  },
  DAYS_PER_YEAR: {
    value: /^(?:[1-9]\d?|[12]\d{2}|3[0-5]\d|36[0-6])$/,
    message: t("validationErrors.daysPerYear"),
  },
  POSITIVE_NUMBER: {
    value: /^(?=.*[1-9])\d*(?:\.\d{1,3})?$/,
    message: t("validationErrors.positiveNumber"),
  },
  POSITIVE_INTEGER: {
    value: /^[1-9]\d*$/,
    message: t("validationErrors.positiveInteger"),
  },
});

export const netZeroStrategySvgIcon = (props) => {
  return (
    <svg
      className={props.className}
      width="80"
      height="80"
      viewBox="0 0 80 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6.66663 73.3331V66.6664C6.66663 66.6664 23.3333 59.9998 40 59.9998C56.6666 59.9998 73.3333 66.6664 73.3333 66.6664V73.3331H6.66663ZM37.6666 30.3331C33.6666 17.3331 13.3333 20.3331 13.3333 20.3331C13.3333 20.3331 14 46.3331 33 42.3331C31.6666 32.6664 26.6666 29.9998 26.6666 29.9998C36 29.9998 36.6666 41.3331 36.6666 41.3331V56.6664H43.3333V42.6664C43.3333 42.6664 43.3333 29.6664 53.3333 26.3331C53.3333 26.3331 46.6666 36.3331 46.6666 42.9998C70 45.3331 70 13.3331 70 13.3331C70 13.3331 40.3333 9.99977 37.6666 30.3331Z"
        fill="currentColor"
      />
    </svg>
  );
};

export const carbonDioxideSvgIcon = (props) => {
  return (
    <svg
      className={props.className}
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2.44286 4C1.92758 4 1.43341 4.19156 1.06905 4.53253C0.704693 4.87351 0.5 5.33597 0.5 5.81818V11.2727C0.5 11.7549 0.704693 12.2174 1.06905 12.5584C1.43341 12.8994 1.92758 13.0909 2.44286 13.0909H5.35714V11.2727H2.44286V5.81818H5.35714V4H2.44286ZM8.27143 4C7.75615 4 7.26198 4.19156 6.89762 4.53253C6.53327 4.87351 6.32857 5.33597 6.32857 5.81818V11.2727C6.32857 11.7549 6.53327 12.2174 6.89762 12.5584C7.26198 12.8994 7.75615 13.0909 8.27143 13.0909H10.2143C10.7296 13.0909 11.2237 12.8994 11.5881 12.5584C11.9524 12.2174 12.1571 11.7549 12.1571 11.2727V5.81818C12.1571 5.33597 11.9524 4.87351 11.5881 4.53253C11.2237 4.19156 10.7296 4 10.2143 4H8.27143ZM8.27143 5.81818H10.2143V11.2727H8.27143V5.81818ZM13.1286 7.18182V8.54545H16.0429V9.90909H14.5857C14.1993 9.90909 13.8286 10.0528 13.5554 10.3085C13.2821 10.5642 13.1286 10.9111 13.1286 11.2727V14H17.5V12.6364H14.5857V11.2727H16.0429C16.4293 11.2727 16.7999 11.1291 17.0732 10.8733C17.3465 10.6176 17.5 10.2708 17.5 9.90909V8.54545C17.5 8.1838 17.3465 7.83695 17.0732 7.58122C16.7999 7.32549 16.4293 7.18182 16.0429 7.18182H13.1286Z"
        fill="currentColor"
      />
    </svg>
  );
};

export const SUPPLIER_QUESTIONNAIRE_TYPES = Object.freeze({
  CONSUMPTION_BASED: "CONSUMPTION_BASED",
  EMISSION_BASED: "EMISSION_BASED",
  DECARBONIZATION_NET_ZERO_PLAN: "DECARBONIZATION_NET_ZERO_PLAN",
});

export const QUARTERS = Object.freeze({
  ALL: "ALL",
  Q1: "Q1",
  Q2: "Q2",
  Q3: "Q3",
  Q4: "Q4",
});

export const questionBookSvgIcon = () => {
  return (
    <svg width="21" height="24" viewBox="0 0 21 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M18.8599 18.4286H18.4313V21.4286H18.8599C19.2009 21.4286 19.5279 21.564 19.769 21.8051C20.0102 22.0463 20.1456 22.3733 20.1456 22.7143C20.1456 23.0553 20.0102 23.3823 19.769 23.6234C19.5279 23.8645 19.2009 24 18.8599 24H4.50276C3.42295 24 2.38737 23.571 1.62383 22.8075C0.860288 22.044 0.431335 21.0084 0.431335 19.9286V3.85714C0.431335 2.83417 0.837712 1.85309 1.56107 1.12973C2.28442 0.406376 3.2655 0 4.28848 0L16.2885 0C17.3115 0 18.2925 0.406376 19.0159 1.12973C19.7392 1.85309 20.1456 2.83417 20.1456 3.85714V17.1429C20.1456 17.4839 20.0102 17.8109 19.769 18.052C19.5279 18.2931 19.2009 18.4286 18.8599 18.4286ZM4.50276 18.4286H15.8599V21.4286H4.50276C4.10494 21.4286 3.72341 21.2705 3.4421 20.9892C3.1608 20.7079 3.00276 20.3264 3.00276 19.9286C3.00276 19.5307 3.1608 19.1492 3.4421 18.8679C3.72341 18.5866 4.10494 18.4286 4.50276 18.4286ZM10.5816 4.84971C10.3641 4.80636 10.1396 4.8118 9.92444 4.86564C9.70925 4.91947 9.50868 5.02036 9.33717 5.16104C9.16567 5.30172 9.0275 5.47869 8.93262 5.67919C8.83774 5.8797 8.78851 6.09875 8.78848 6.32057C8.78848 6.60473 8.6756 6.87725 8.47466 7.07819C8.27373 7.27912 8.00121 7.392 7.71705 7.392C7.43289 7.392 7.16037 7.27912 6.95943 7.07819C6.7585 6.87725 6.64562 6.60473 6.64562 6.32057C6.64562 5.64633 6.83274 4.98531 7.18615 4.41112C7.53956 3.83692 8.04539 3.37207 8.64734 3.06831C9.24928 2.76456 9.92372 2.63383 10.5956 2.69066C11.2674 2.7475 11.9103 2.98968 12.4527 3.39024C12.995 3.7908 13.4156 4.33402 13.6675 4.95942C13.9194 5.58483 13.9928 6.26789 13.8796 6.93255C13.7663 7.59721 13.4708 8.21739 13.0259 8.72405C12.5811 9.23072 12.0043 9.60398 11.3599 9.80229C11.3529 10.0864 11.2332 10.3562 11.0273 10.5521C10.8214 10.7481 10.5461 10.8542 10.2619 10.8471C9.97775 10.8401 9.70802 10.7205 9.51207 10.5145C9.31613 10.3086 9.21 10.0333 9.21705 9.74914V8.892C9.21705 8.30057 9.69705 7.82057 10.2885 7.82057C10.6605 7.81988 11.0189 7.68099 11.2943 7.43086C11.5696 7.18074 11.7422 6.83723 11.7785 6.46702C11.8148 6.0968 11.7123 5.7263 11.4908 5.42744C11.2693 5.12858 10.9464 4.92268 10.5816 4.84971ZM10.2885 15.2297C9.89065 15.2297 9.50912 15.0717 9.22782 14.7904C8.94651 14.5091 8.78848 14.1275 8.78848 13.7297C8.78848 13.3319 8.94651 12.9504 9.22782 12.6691C9.50912 12.3877 9.89065 12.2297 10.2885 12.2297C10.6863 12.2297 11.0678 12.3877 11.3491 12.6691C11.6304 12.9504 11.7885 13.3319 11.7885 13.7297C11.7885 14.1275 11.6304 14.5091 11.3491 14.7904C11.0678 15.0717 10.6863 15.2297 10.2885 15.2297Z"
        fill="#0C3136"
      />
    </svg>
  );
};

function getLocale() {
  // All modern browsers support this. Should match what's used by localeCompare() etc.
  const intl = window.Intl;
  if (intl !== undefined) {
    return intl.NumberFormat().resolvedOptions().locale;
  }
  // Fall back to ranked choice locales, which are configured in the browser but aren't necessarily
  // what's used in functions like localeCompare().
  const languages = navigator.languages;
  if (languages !== undefined && languages.length > 0) {
    return languages[0];
  }
  // Old standard.
  return navigator.language ?? "en-US";
}

/**
 * Gets the user's current timezone from the browser
 * @returns {string} IANA timezone identifier (e.g., 'Europe/London', 'America/New_York')
 */
function getCurrentTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.warn("Failed to get timezone from browser:", error);
    return "UTC";
  }
}

// Static formatters for efficient date formatting
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString
export const locale = getLocale();
const defaultFormatter = new Intl.DateTimeFormat(locale, {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  timeZone: getCurrentTimezone(),
});

const defaultWithTimeFormatter = new Intl.DateTimeFormat(locale, {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: getCurrentTimezone(),
});

const weekdayFormatter = new Intl.DateTimeFormat(locale, {
  weekday: "short",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  timeZone: getCurrentTimezone(),
});

const weekdayWithTimeFormatter = new Intl.DateTimeFormat(locale, {
  weekday: "short",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: getCurrentTimezone(),
});

/**
 * Handles timezone and locale for date formatting
 * @param {string} dateVal
 * @param {boolean} includeHours
 * @param {boolean} defaultType true if the date should be formatted as DD/MM/YYYY, false if it should be formatted as ddd, DD MMM YY
 * @returns {string} a human date string for display
 */
export const formatDate = (dateVal, includeHours = true, defaultType = true) => {
  if (!dateVal) return "";

  const date = new Date(dateVal);

  if (defaultType) {
    return includeHours ? defaultWithTimeFormatter.format(date) : defaultFormatter.format(date);
  }

  return includeHours ? weekdayWithTimeFormatter.format(date) : weekdayFormatter.format(date);
};

/**
 *
 * @param {Date|string|number|null|undefined} dateVal
 * @returns {Date|null}
 */
export const toDate = (dateVal) => {
  if (!dateVal) return null;
  if (typeof dateVal === "string") {
    return new Date(dateVal.split("T")[0]);
  }
  return new Date(dateVal);
};

export const getFormattedNumValue = (value, maximumFractionDigits = 4, withMillionSuffix = false) => {
  if (value === null || value === undefined || isNaN(value)) return "N/A";
  if (withMillionSuffix && Math.abs(value) >= 1_000_000) {
    let millionValue = value / 1_000_000;
    return Number(millionValue).toLocaleString("en-GB", { maximumFractionDigits }) + "M";
  } else {
    return Number(value).toLocaleString("en-GB", { maximumFractionDigits });
  }
};

export const sortAlphabeticallyByAttribute = (a, b, attribute) => {
  const itemA = a[attribute]?.toLowerCase();
  const itemB = b[attribute]?.toLowerCase();

  if (itemA < itemB) return -1;
  if (itemA > itemB) return 1;

  return 0;
};

export const SORT_DIRECTIONS = Object.freeze({
  ASC: "asc",
  DESC: "desc",
  NONE: "none",
});

export const getSortDirection = (sortingPairs, accessorName) => {
  const foundSortString = sortingPairs.find((el) => {
    return el.split(",")[0] === accessorName;
  });
  return foundSortString ? foundSortString.split(",")[1] : SORT_DIRECTIONS.NONE;
};

// Extracted from react-table library
export const useAsyncDebounce = (defaultFn, defaultWait = 0) => {
  function useGetLatest(obj) {
    const ref = React.useRef();
    ref.current = obj;

    return React.useCallback(() => ref.current, []);
  }

  const debounceRef = React.useRef({});

  const getDefaultFn = useGetLatest(defaultFn);
  const getDefaultWait = useGetLatest(defaultWait);

  return React.useCallback(
    async (...args) => {
      if (!debounceRef.current.promise) {
        debounceRef.current.promise = new Promise((resolve, reject) => {
          debounceRef.current.resolve = resolve;
          debounceRef.current.reject = reject;
        });
      }

      if (debounceRef.current.timeout) {
        clearTimeout(debounceRef.current.timeout);
      }

      debounceRef.current.timeout = setTimeout(async () => {
        delete debounceRef.current.timeout;
        try {
          debounceRef.current.resolve(await getDefaultFn()(...args));
        } catch (err) {
          debounceRef.current.reject(err);
        } finally {
          delete debounceRef.current.promise;
        }
      }, getDefaultWait());

      return debounceRef.current.promise;
    },
    [getDefaultFn, getDefaultWait]
  );
};

export const ELECTRICITY_TYPES = Object.freeze({
  ELECTRICITY_LOCATION_BASED: "ELECTRICITY_LOCATION_BASED",
  ELECTRICITY_MARKET_BASED: "ELECTRICITY_MARKET_BASED",
  RENEWABLE_ENERGY_PROCUREMENT: "RENEWABLE_ENERGY_PROCUREMENT",
});

export const CALCULATION_BOUNDARY_OPTIONS = Object.freeze({
  OPERATIONAL_CONTROL: "OPERATIONAL_CONTROL",
  FINANCIAL_CONTROL: "FINANCIAL_CONTROL",
  EQUITY_SHARE: "EQUITY_SHARE",
});

export const CATEGORIES_NUM_KEYS = [
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
  "Eleven",
  "Twelve",
  "Thirteen",
  "Fourteen",
  "Fifteen",
];

export const SUPPLIER_TYPES = Object.freeze({
  SUPPLIER: "SUPPLIER",
  SUPPLIER_VIEWER: "SUPPLIER_VIEWER",
});

export const CSRD_QUESTIONNAIRE_TYPES = Object.freeze({
  ENVIRONMENTAL: "ENVIRONMENTAL",
  SOCIAL: "SOCIAL",
  GOVERNANCE: "GOVERNANCE",
});

/**
 * TODO document
 * @param {*} prevState
 * @param {*} newValObject
 * @param {*} sortAccessors
 * @param {*} initialSortingParam
 * @returns
 */
export const calculateDataTableState = ({ prevState, newValObject, sortAccessors = [], initialSortingParam }) => {
  let changedAttributes = {};

  Object.keys(newValObject).forEach((dataTableAttribute) => {
    const value = newValObject[dataTableAttribute];

    if (dataTableAttribute === "filterObj") {
      // We have to save not only the new value sent from the data table
      // but also the previously applied filters, which we get from prevState
      changedAttributes[dataTableAttribute] = {
        ...prevState[dataTableAttribute],
        [value.attribute]: value.value,
      };
    } else if (dataTableAttribute === "queryPageSortBy") {
      let stringValue = value[0];
      let accessorName = stringValue.split(",")[0];
      const sortDirection = stringValue.split(",")[1];
      const updatedArray = [...prevState[dataTableAttribute]];

      sortAccessors.forEach((el) => {
        if (accessorName === el.originalAccessor) {
          // alternativeAccessor value is the one used by the back-end for sorting
          stringValue = `${el.alternativeAccessor},${sortDirection}`;
          accessorName = el.alternativeAccessor;
        }
      });

      if (initialSortingParam) {
        // Removing initial sorting by "initialSortingParam", if still available
        const indexOfInitial = updatedArray.findIndex((el) => {
          return el.split(",")[0] === initialSortingParam;
        });
        if (indexOfInitial > -1) {
          updatedArray.splice(indexOfInitial, 1);
        }
      }

      const indexOfExisting = updatedArray.findIndex((el) => {
        return el.split(",")[0] === accessorName;
      });
      if (indexOfExisting > -1) {
        // Column which we try to sort is already in the sort list, so we just manipulate it's record
        if (sortDirection === SORT_DIRECTIONS.NONE) {
          updatedArray.splice(indexOfExisting, 1);
        } else {
          updatedArray[indexOfExisting] = stringValue;
        }
      } else {
        // Column which we try to sort does not exist in the sort list, so we add it
        updatedArray.push(stringValue);
      }

      changedAttributes[dataTableAttribute] = updatedArray;
    } else {
      // All other values have primitive types, so we apply them directly
      // without checking their previous states
      changedAttributes[dataTableAttribute] = value;
    }
  });

  return changedAttributes;
};

export const handlePageSizeChange = (data, queryPageIndex, manageDataTableState) => {
  if (data?.content?.length === 0 && data?.number !== 0 && data?.totalElements !== 0 && queryPageIndex > 0) {
    // Case when page size is changed manually and the user needs to be redirected to the previous page
    // so they don't see a page with no elements in it
    manageDataTableState({
      totalElements: data?.totalElements ?? 0,
      totalPages: data?.totalPages ?? 0,
      queryPageIndex: queryPageIndex - 1,
    });
  } else {
    manageDataTableState({
      totalElements: data?.totalElements ?? 0,
      totalPages: data?.totalPages ?? 0,
    });
  }
};

export const handleFileDownload = (data, headers, defaultName) => {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement("a");
  const defaultFileName = headers["content-disposition"]?.split("filename=")?.[1] ?? defaultName;
  link.href = url;
  link.setAttribute("download", defaultFileName);
  document.body.appendChild(link);
  link.click();
  return true;
};

/**
 * Convert an object to a URL params string.
 * @param {object} params {key:value}
 * @returns url-encoded string, e.g. "key1=value%201&key2=value%202"
 * Note: a=[b] becomes a=b.
 */
export function urlParamsString(params) {
  // filter null values
  const filteredParams = Object.fromEntries(
    Object.entries(params).filter(([_, value]) => value !== null && value !== undefined)
  );
  return new URLSearchParams(filteredParams).toString();
}

/**
 * Prefer urlParamsString() except where one key: multiple values is required
 * Join parameters into a url query/search string
 * @param {Object} queryParams {key:value} This object is emptied out as a side effect??
 * @param {*} multipleValuesAttributes
 * @returns {!string}
 */
export const constructQueryParamsString = (queryParams, multipleValuesAttributes = []) => {
  if (!queryParams) {
    return "";
  }
  // TODO rewrite using URLSearchParams() and append
  // "sort" params and "multipleValuesAttributes" are extracted from queryParams object, because they can have more than one value.
  // To avoid overriding of those multiple values which have to share the same key, we put them
  // together in a string called "multipleValuesParams"
  let multipleValuesParams = "";
  Object.keys(queryParams).forEach((param) => {
    if (!is(queryParams[param]) || queryParams[param] === "" || queryParams[param].length === 0) {
      // Removing empty params
      delete queryParams[param];
    } else if (param === "sort") {
      queryParams[param].forEach((sortStr) => {
        multipleValuesParams += `&sort=${sortStr}`;
      });
      delete queryParams[param];
    } else {
      multipleValuesAttributes.forEach((attrObj) => {
        if (param === attrObj.defaultAttribute) {
          queryParams[param].forEach((obj) => {
            multipleValuesParams += `&${attrObj.multipleValuesAttribute}=${urlEncode(obj.value)}`;
          });
          delete queryParams[param];
        }
      });
    }
  });

  return `${new URLSearchParams(queryParams).toString()}${multipleValuesParams}`;
};

export const handleMultiLineImportErrors = (err, toast) => {
  if (err.response?.status === 400) {
    const errorsArray = JSON.parse(err.response?.data?.parameters?.message);
    if (errorsArray && errorsArray.length > 0) {
      const stackedErrors = (
        <Stack spacing={1}>
          {errorsArray.map((el, index) => {
            return <Text key={`error-text-${index}`}>{`${el.subject ?? ""} ${el.details ?? ""}`}</Text>;
          })}
        </Stack>
      );
      showErrorToast(toast, stackedErrors);
    } else {
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
    }
  } else {
    showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
  }
};

export const formatDateToSegments = (dateStr) => {
  const now = new Date();
  const date = new Date(dateStr);
  const secondsAgo = Math.floor((now - date) / 1000);

  if (secondsAgo < 60) return `${secondsAgo} secs ago`;
  const minutesAgo = Math.floor(secondsAgo / 60);
  if (minutesAgo < 60) return `${minutesAgo} mins ago`;
  const hoursAgo = Math.floor(minutesAgo / 60);
  if (hoursAgo < 24) return `${hoursAgo} hrs ago`;
  const daysAgo = Math.floor(hoursAgo / 24);
  if (daysAgo < 30) return `${daysAgo} days ago`;
  const monthsAgo = Math.floor(daysAgo / 30);
  if (monthsAgo < 12) return `${monthsAgo} months ago`;
  const yearsAgo = Math.floor(monthsAgo / 12);
  return `${yearsAgo} years ago`;
};
